import { apiClient } from "./apiClient";

const auth = (accessToken) => ({ headers: { Authorization: `Bearer ${accessToken}` } });

// Paginated list -> {items, total} (total from X-Total-Count), same
// convention as edgeApi.listEdges.
async function paginated(accessToken, url, params) {
  const res = await apiClient.get(url, { ...auth(accessToken), params });
  return { items: res.data, total: Number(res.headers?.["x-total-count"] ?? res.data.length) };
}

/** List immutable signed OS releases, newest first. */
export const listOtaReleases = (accessToken, params) =>
  paginated(accessToken, "/admin/ota/releases", params);

/** Publish metadata for a signed bundle already staged on the server. */
export const publishOtaRelease = (accessToken, payload) =>
  apiClient.post("/admin/ota/releases", payload, auth(accessToken));

/** Upload a signed bundle (raw body, streamed). Type and version come from the file name, the server hashes it
 * while writing and publishes the release. onProgress({loaded, total}); signal aborts the upload. */
export const uploadOtaRelease = (accessToken, file, meta, { onProgress, signal } = {}) =>
  apiClient.put("/admin/ota/releases/upload", file, {
    params: {
      filename: file.name,
      release_notes: meta.release_notes,
      channel: meta.channel,
      from_version: meta.from_version || undefined,
      recovery_safe: meta.recovery_safe || undefined,
    },
    headers: { ...auth(accessToken).headers, "Content-Type": "application/octet-stream" },
    onUploadProgress: (e) => onProgress?.({ loaded: e.loaded, total: e.total ?? file.size }),
    signal,
    timeout: 0,
    maxBodyLength: Infinity,
    maxContentLength: Infinity,
  });

/** List global, tenant, and edge rollout state, newest first. */
export const listOtaRollouts = (accessToken, params) =>
  paginated(accessToken, "/admin/ota/rollouts", params);

/** Start or upsert a canary-aware rollout. */
export const createOtaRollout = (accessToken, payload) =>
  apiClient.post("/admin/ota/rollouts", payload, auth(accessToken));

/** Pause/resume and optionally change canary percentage. */
export const setOtaRolloutStatus = (accessToken, rolloutId, payload) =>
  apiClient.patch(`/admin/ota/rollouts/${rolloutId}`, payload, auth(accessToken));

/** Delete a rollout assignment. */
export const deleteOtaRollout = (accessToken, rolloutId) =>
  apiClient.delete(`/admin/ota/rollouts/${rolloutId}`, auth(accessToken));

/** Fleet OS version vs target for Edge management. */
export const listOtaFleetStatus = (accessToken, params) =>
  paginated(accessToken, "/admin/ota/fleet-status", params);

/** Mark/unmark a release as a valid unattended-recovery target. */
export const setOtaReleaseRecoverySafe = (accessToken, releaseId, recoverySafe) =>
  apiClient.patch(`/admin/ota/releases/${releaseId}`, { recovery_safe: recoverySafe }, auth(accessToken));

/** Edit release metadata (from_version, channel, release_notes); the uploaded file stays. */
export const updateOtaRelease = (accessToken, releaseId, patch) =>
  apiClient.patch(`/admin/ota/releases/${releaseId}`, patch, auth(accessToken));

/** Revoke a release: releases are immutable (audit trail, devices may already
 * record "applied"), so there is no hard delete -- revoke pauses every active
 * rollout on it in one transaction and it stops being offered to any edge. */
export const revokeOtaRelease = (accessToken, releaseId) =>
  apiClient.post(`/admin/ota/releases/${releaseId}/revoke`, {}, auth(accessToken));

/** Delete a revoked release, freeing its version string for reuse. */
export const deleteOtaRelease = (accessToken, releaseId) =>
  apiClient.delete(`/admin/ota/releases/${releaseId}`, auth(accessToken));

export const getSecurityOverview = (accessToken) =>
  apiClient.get("/admin/security/overview", auth(accessToken)).then((res) => res.data);

export const listSecurityAdvisories = (accessToken, params) =>
  apiClient.get("/admin/security/advisories", { ...auth(accessToken), params }).then((res) => res.data);

export const listSecurityAffectedEdges = (accessToken, advisoryId) =>
  apiClient.get(`/admin/security/advisories/${advisoryId}/affected-edges`, auth(accessToken)).then((res) => res.data);

/** Open findings per OS / stack version (with scan state and device count). */
export const listSecurityVersions = (accessToken) =>
  apiClient.get("/admin/security/versions", auth(accessToken)).then((res) => res.data);

/** Findings of one version, or of one device: {items, total}. */
export const listSecurityVersionAdvisories = (accessToken, version, params) =>
  paginated(accessToken, `/admin/security/versions/${encodeURIComponent(version)}/advisories`, params);

/** Devices with the findings of their OS + stack version, worst first: {items, total}. */
export const listSecurityEdgeExposure = (accessToken, params) =>
  paginated(accessToken, "/admin/security/edges", params);

export const listSecurityEdgeAdvisories = (accessToken, edgeId, params) =>
  paginated(accessToken, `/admin/security/edges/${edgeId}/advisories`, params);

/** What a security rollout for these categories (e.g. ["kev","critical"]) would cover. */
export const getSecurityRolloutPreview = (accessToken, categories) =>
  apiClient.get("/admin/security/rollout-preview", { ...auth(accessToken), params: { categories: categories.join(",") } }).then((res) => res.data);

/** ONE rollout of a fixed release for everything the chosen categories cover. */
export const createSecurityCategoryRollout = (accessToken, payload) =>
  apiClient.post("/admin/security/rollouts", payload, auth(accessToken)).then((res) => res.data);

/** Notify targeted tenants after an operator explicitly confirms the security rollout email. */
export const notifySecurityRollout = (accessToken, rolloutId) =>
  apiClient.post(`/admin/security/rollouts/${rolloutId}/notify`, {}, auth(accessToken)).then((res) => res.data);

export const createSecurityRollout = (accessToken, advisoryId, payload) =>
  apiClient.post(`/admin/security/advisories/${advisoryId}/rollouts`, payload, auth(accessToken));

export const scanSecuritySbom = (accessToken, file, osVersion) => {
  const form = new FormData();
  form.append("sbom", file);
  form.append("os_version", osVersion);
  return apiClient.post("/admin/security/scan-sbom", form, {
    ...auth(accessToken),
  });
};

export const getSecurityScanJob = (accessToken, jobId) =>
  apiClient.get(`/admin/security/scan-jobs/${jobId}`, auth(accessToken)).then((res) => res.data);

/** Rescan every published OS release that has an SBOM (the server also does this daily). */
export const scanSecurityReleases = (accessToken) =>
  apiClient.post("/admin/security/scan-releases", {}, auth(accessToken)).then((res) => res.data);
